'use client';
// ct2 design variant (?v=ct2) — the floating header (logo) + menu button in the bottom dock (RTL start) + the drawer (category icons + city emblems)
// + the bottom dock (המומחים / מחירים / שאלות / ייעוץ). It replaces the site header, the bottom consult bar and the consult float on THIS page only.
// City pick drives the page's own list through the ONE city door (lib/smartIntakeStore setKnownCity), exactly like the site menu search.
import { useEffect, useMemo, useRef, useState } from 'react';
import SmartIntakeBar from '../SmartIntakeBar';
import { setKnownCity, useSmartIntakeCtx, resolvePageCity } from '@/lib/smartIntakeStore';
import { findCity } from '@/lib/israelCities';
import { consultHref } from '@/lib/consultLink.mjs'; // same server door as ConsultFloat: ticket + Ads conversion + 302 to our WhatsApp
import { getSessionId } from '@/lib/trackerSession';
import { getConsultDraft } from '@/lib/quickConsultStore';
import { X3D_GOOGLE_WORDMARK } from '@/lib/x3dHeader'; // same Google wordmark as the 3D pages header
import CITIES from '@/lib/ct2Cities.json';
import type { Ct2Cat } from '@/lib/ct2Menu';
import { avatarImg } from '@/lib/imgUrl';

type CityRow = { he: string; icon: string; region?: string; slug?: string; site?: boolean };
const TOP_CITIES = 12;

// Pre-ct2 menu content (components/MobileMenu.tsx): pages, "למה אנחנו?" tiles, legal line — same labels and links.
const PAGES: [string, string][] = [['🔎 קטגוריות', '/קטגוריה'], ['📚 מדריכים ומאמרים', '/מאמרים'], ['ℹ️ אודות', '/אודות'], ['✉️ צור קשר', '/צור-קשר']];
const LEGAL: [string, string][] = [['🔐 כניסת בעלי עסקים', '/כניסת-בעלי-עסקים'], ['פרטיות', '/פרטיות'], ['תנאי שימוש', '/תנאים']];
const WHY: { icon: string; label: string }[] = [
  { icon: 'why-objective', label: 'המלצות אובייקטיביות בלבד' }, { icon: 'why-verified', label: 'המלצות מאומתות בלבד' },
  { icon: 'why-free', label: 'זה בחינם' }, { icon: 'why-fast', label: 'תגובה מהירה' }, { icon: 'why-love-red', label: 'נעשה באהבה' },
];

// ONE chrome for every public page (Founder 15/10). Category pages pass categoryName/cityName/hasPrices/hasFaq/catSlug (unchanged);
// every other page passes `site` = { items (the dock between the menu and ייעוץ), consultCat, articleId }.
export type Ct2Icon = 'user' | 'price' | 'faq' | 'star' | 'photo' | 'doc' | 'grid';
export type Ct2Item = { key: string; label: string; icon: Ct2Icon; id?: string; href?: string };
export type Ct2Site = { items: Ct2Item[]; consultCat?: string | null; currentCat?: string | null; /** desktop ≥1024: dock items move into the header (pro pages, 16/10/26) */ deskNav?: boolean; /** article pages with Ct2Assistant (21/10/26): no dock, the menu sits in the header like category pages */ noDock?: boolean; /** pro pages (Founder 21/10/26): PHONES <768 have no floating dock — the menu moves into the header (.ct2-mob-menu), ProFloatingContact floats the pro bubble */ mobNoDock?: boolean };
const ICON_PATHS: Record<Ct2Icon, React.ReactNode> = {
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" /></>,
  price: <><text x="12" y="19" textAnchor="middle" fontSize="22" fontWeight="800" fill="currentColor" stroke="none" style={{ fontFamily: 'inherit' }}>₪</text></>,
  faq: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5M12 17h.01" /></>,
  star: <path d="M12 3l2.7 5.8 6.3.7-4.7 4.3 1.3 6.2L12 16.8 6.4 20l1.3-6.2L3 9.5l6.3-.7z" />,
  photo: <><rect x="3.5" y="5" width="17" height="14" rx="2.5" /><circle cx="9" cy="10.5" r="1.8" /><path d="M4 17l5-4.5 3.5 3 3-2.5 4.5 4" /></>,
  doc: <><path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10.5a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z" /><path d="M14 3.5V8h4M9.5 12h5M9.5 15.5h5" /></>,
  grid: <><rect x="4" y="4" width="6.5" height="6.5" rx="1.5" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.5" /></>,
};
const sw = (i: Ct2Icon) => (i === 'price' ? '2.2' : '2');

export default function Ct2Chrome({ categoryName = '', cityName = null, hasPrices = false, hasFaq = false, cats, catSlug = '', site }: { categoryName?: string; cityName?: string | null; hasPrices?: boolean; hasFaq?: boolean; cats: Ct2Cat[]; catSlug?: string; site?: Ct2Site }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const [q, setQ] = useState('');
  const [deskMq, setDeskMq] = useState(false); // ≥1024: the header carries the smart search (pro pages)
  useEffect(() => { const m = window.matchMedia('(min-width:1024px)'); const f = () => setDeskMq(m.matches); f(); m.addEventListener('change', f); return () => m.removeEventListener('change', f); }, []);
  const btnRef = useRef<HTMLButtonElement>(null);
  const topMenuRef = useRef<HTMLButtonElement>(null);
  const mobMenuRef = useRef<HTMLButtonElement>(null); // pro pages <768: the header menu (the dock is hidden there)
  const closeRef = useRef<HTMLButtonElement>(null);
  const intake = useSmartIntakeCtx();
  const catPlain = (cats.find(c => c.slug === catSlug)?.name || categoryName.replace(/ ב.+$/, '')).trim(); // plain category NAME, no city
  // site pages: which dock targets really exist on THIS page (a jump to a missing / hidden section would be a dead button)
  const [live, setLive] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!site) return;
    const vis = (id: string) => { const el = document.getElementById(id); return !!el && (el as HTMLElement).offsetParent !== null; };
    const check = () => setLive(new Set(site.items.filter(i => { if (!i.id) return true; return vis(i.id) || vis(i.id + '-d'); }).map(i => i.key)));
    check();
    const ts = [setTimeout(check, 700), setTimeout(check, 2500)];
    window.addEventListener('resize', check);
    return () => { ts.forEach(clearTimeout); window.removeEventListener('resize', check); };
  }, [site]);
  const siteCats = useMemo(() => {
    if (!site) return [] as Ct2Cat[];
    const t = q.trim();
    const list = t ? cats.filter(c => c.name.includes(t)) : (all ? cats : cats.slice(0, 12));
    return list;
  }, [site, cats, q, all]);
  const picked = resolvePageCity(intake) || cityName || null;

  // the heading city chip (Ct2Heading) opens this drawer — its city list IS the city switcher (bughunter 15/10: chip was dead)
  useEffect(() => {
    const o = () => setOpen(true);
    window.addEventListener('ct2:open-cities', o);
    return () => window.removeEventListener('ct2:open-cities', o);
  }, []);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', k);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => { window.removeEventListener('keydown', k); document.body.style.overflow = prev; ([topMenuRef.current, mobMenuRef.current].find(b => b && b.offsetParent) || btnRef.current)?.focus(); };
  }, [open]);

  const cities = CITIES as CityRow[];
  const shown = useMemo(() => {
    const t = q.trim();
    if (t) return cities.filter(c => c.he.includes(t)).slice(0, 60);
    const base = all ? cities : cities.slice(0, TOP_CITIES);
    const p = picked ? cities.find(c => c.he === picked) : null; // the chosen city keeps its icon tile, first (Founder 15/10)
    return p ? [p, ...base.filter(c => c !== p)] : base;
  }, [q, all, cities, picked]);
  const pickedRow = picked ? cities.find(c => c.he === picked) || null : null;

  function pickCity(c: CityRow) {
    if (c.site && c.slug) { location.href = `/${catSlug}/${c.slug}?v=ct2`; return; } // real city route supports ct2 — keep the variant
    const f = findCity(c.he);
    setKnownCity(c.he, f ? { lat: f.lat, lng: f.lng } : null);
    setOpen(false);
    setTimeout(() => document.getElementById('experts-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }
  const jump = (id: string) => {
    setOpen(false); // bughunter 15/10: a dock tap with the drawer open must close it
    // desktop ≥1024 (pro pages): a section may live in a desktop-only twin '<id>-d' (the mobile one is display:none there)
    const el = (window.matchMedia('(min-width:1024px)').matches && document.getElementById(id + '-d')) || document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.classList.remove('ct2-flash'); void el.offsetWidth; el.classList.add('ct2-flash'); // visible feedback even when already in view
    setTimeout(() => el.classList.remove('ct2-flash'), 1300);
  };
  const consult = () => {
    const sid = (() => { try { return getSessionId(); } catch { return null; } })();
    if (site) {
      const cc = site.consultCat || null;
      const draft = cc ? getConsultDraft(cc) : null;
      const art = document.querySelector('[data-article-id]')?.getAttribute('data-article-id');
      location.href = consultHref({ cat: cc, city: draft?.city || null, need: draft?.need || null, article: art || null, sid, src: 'float' });
      return;
    }
    const draft = getConsultDraft(catSlug);
    location.href = consultHref({ cat: catSlug, city: draft?.city || picked || cityName || null, need: draft?.need || null, sid, src: 'float' });
  };

  return (
    <>
      <header className="ct2-top" dir="rtl">
        {site?.deskNav && (
          <nav className="ct2-topnav" aria-label="תפריט">
            <button ref={topMenuRef} type="button" className="ct2-topnav-menu" aria-label={open ? "סגירת תפריט" : "פתיחת תפריט"} aria-expanded={open} aria-controls="ct2-drawer" onClick={() => setOpen(o => !o)}>
              <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg><span>תפריט</span>
            </button>
          </nav>
        )}
        {(!site || site.noDock) && (
          <button ref={topMenuRef} type="button" className="ct2-hdr-menu" aria-label={open ? "סגירת תפריט" : "פתיחת תפריט"} aria-expanded={open} aria-controls="ct2-drawer" onClick={() => setOpen(o => !o)}>
            <svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
        )}
        {site?.mobNoDock && !site.noDock && (
          <button ref={mobMenuRef} type="button" className="ct2-mob-menu" aria-label={open ? "סגירת תפריט" : "פתיחת תפריט"} aria-expanded={open} aria-controls="ct2-drawer" onClick={() => setOpen(o => !o)}>
            <svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
        )}
        <a href="/" className="ct2-logo" aria-label="המומחים — דף הבית">
          <img src="/brand/logo-white.webp" alt="המומחים" width={227} height={38} />
        </a>
        {site?.deskNav && deskMq && !(site.consultCat || site.currentCat) /* Founder 20/10/26: inside a category the header = menu + logo + slogan, no smart search */ && <div className="ct2-topsearch"><SmartIntakeBar inline /></div>}
        <span className="ct2-tagl"><span>רק בעלי עסקים</span><span className="ct2-tagl-r">עם דירוג <bdi dir="ltr">4.5+</bdi> <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="#FBBC04"><path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z"/></svg> ב<span className="ct2-gw" dangerouslySetInnerHTML={{ __html: X3D_GOOGLE_WORDMARK }} /></span></span>
      </header>

      <div className={`ct2-backdrop${open ? ' on' : ''}`} onClick={() => setOpen(false)} aria-hidden="true" />
      <aside id="ct2-drawer" className={`ct2-drawer${open ? ' on' : ''}${site ? '' : ' ct2-drawer-s'}`} dir="rtl" role="dialog" aria-modal="true" aria-label="תפריט" aria-hidden={!open} {...(open ? {} : { inert: true } as any)}>
        <div className="ct2-dr-head">
          <b>תפריט</b>
          <button ref={closeRef} type="button" className="ct2-dr-close" onClick={() => setOpen(false)} aria-label="סגירת תפריט">✕</button>
        </div>
        <div className="ct2-dr-body">
          {site ? (<>
          <h2 className="ct2-dr-h">קטגוריות</h2>
          <input className="ct2-dr-search" type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="חיפוש קטגוריה" aria-label="חיפוש קטגוריה" />
          <div className="ct2-cats">
            {siteCats.map(c => (
              <a key={c.slug} href={`/${c.slug}`} className="ct2-cat" aria-current={site?.currentCat === c.slug ? 'page' : undefined}>
                {c.icon ? <img {...avatarImg(c.icon, 40)} alt="" aria-hidden="true" width={40} height={40} loading="lazy" decoding="async" /> : <span className="ct2-cat-g" aria-hidden="true">•</span>}
                <span>{c.name}</span>
              </a>
            ))}
            {!siteCats.length && <p className="ct2-empty">לא נמצאה קטגוריה</p>}
          </div>
          {!q.trim() && cats.length > 12 && (
            <button type="button" className="ct2-more" onClick={() => setAll(v => !v)} aria-expanded={all}>{all ? 'הצג פחות קטגוריות' : `עוד קטגוריות (${cats.length - 12})`}</button>
          )}

          </>) : (<>
          <h2 className="ct2-dr-h">{catPlain} לפי עיר{picked ? <span className="ct2-dr-cur">{pickedRow && <img {...avatarImg(pickedRow.icon, 32)} alt="" aria-hidden="true" width={32} height={32} />}נבחרה: {picked}</span> : null}</h2>
          <input className="ct2-dr-search" type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="חיפוש עיר" aria-label="חיפוש עיר" />
          <div className="ct2-cities" role="list">
            {shown.map(c => (
              <button key={c.he} type="button" role="listitem" className="ct2-city" aria-pressed={picked === c.he} onClick={() => pickCity(c)}>
                <img {...avatarImg(c.icon, 36)} alt="" aria-hidden="true" width={36} height={36} loading="lazy" decoding="async" />
                <span>{catPlain}</span><span className="ct2-city-n">ב{c.he}</span>
              </button>
            ))}
            {!shown.length && <p className="ct2-empty">לא נמצאה עיר</p>}
          </div>
          {!q.trim() && cities.length > TOP_CITIES && (
            <button type="button" className="ct2-more" onClick={() => setAll(v => !v)} aria-expanded={all}>{all ? 'הצג פחות ערים' : `עוד ערים (${cities.length - TOP_CITIES})`}</button>
          )}

          </>)}

          <nav aria-label="תפריט האתר" className="ct2-pages">
            {PAGES.map(([label, href]) => <a key={href} href={href} className="ct2-page">{label}</a>)}
          </nav>
          <h2 className="ct2-dr-h ct2-dr-why">למה אנחנו?</h2>
          <ul className="ct2-why">
            {WHY.map(w => (
              <li key={w.icon}>
                <img {...avatarImg(`/img/pricing/${w.icon}.webp`, 34)} alt="" aria-hidden="true" width={34} height={34} loading="lazy" decoding="async" />
                <span>{w.label}</span>
              </li>
            ))}
          </ul>
          <nav aria-label="קישורים נוספים" className="ct2-legal">
            {LEGAL.map(([label, href], i) => (
              <span key={href}>{i > 0 && <span aria-hidden="true" className="ct2-legal-dot">·</span>}<a href={href} className={i === 0 ? 'ct2-legal-main' : undefined}>{label}</a></span>
            ))}
          </nav>
        </div>
      </aside>

      {/* category / city / topic pages (Founder 20/10/26): no dock — the menu sits in the header, Ct2Assistant takes the bottom */}
      {site && !site.noDock && <nav className="ct2-dock" dir="rtl" aria-label="קפיצה בעמוד">
        <button ref={btnRef} type="button" className="ct2-dock-menu" aria-label={open ? "סגירת תפריט" : "פתיחת תפריט"} aria-expanded={open} aria-controls="ct2-drawer" onClick={() => setOpen(o => !o)}>
          <svg aria-hidden="true" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        </button>
        {site ? site.items.filter(i => !live || live.has(i.key)).map(i => {
          const svg = <svg aria-hidden="true" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth={sw(i.icon)} strokeLinecap="round" strokeLinejoin="round">{ICON_PATHS[i.icon]}</svg>;
          return i.href
            ? <button key={i.key} type="button" onClick={() => { setOpen(false); location.href = i.href!; }}>{svg}<span>{i.label}</span></button>
            : <button key={i.key} type="button" onClick={() => jump(i.id!)}>{svg}<span>{i.label}</span></button>;
        }) : <>
        <button type="button" onClick={() => jump('experts-list')}><svg aria-hidden="true" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" /></svg><span>המומחים</span></button>
        {hasPrices && <button type="button" onClick={() => jump('ct2-prices')}><svg aria-hidden="true" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><text x="12" y="19" textAnchor="middle" fontSize="22" fontWeight="800" fill="currentColor" stroke="none" style={{ fontFamily: 'inherit' }}>₪</text></svg><span>מחירים</span></button>}
        {hasFaq && <button type="button" onClick={() => jump('ct2-faq')}><svg aria-hidden="true" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5M12 17h.01" /></svg><span>שאלות</span></button>}
        </>}
        <button type="button" className="ct2-dock-wa" onClick={consult}><svg aria-hidden="true" viewBox="0 0 24 24" width="26" height="26" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 12 12 0 0 0 4.6 4c1.7.7 2.1.6 2.8.5.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.6-.3z" /></svg><span>ייעוץ</span></button>
      </nav>}
    </>
  );
}
